import { IconButton } from '../../ui/controls.js';
import { PathEditor } from '../curves/PathEditor.js';
import type { PointEdit, Vec3 } from '../curves/curve-math.js';
import type { ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, Color, Enum, ListEditor, Num, num, obj, patch, Section, str, Text, vec, Vec, type Obj } from './fields.js';

export type SkinNode = Obj & { name?: string; parent?: string; position?: number[] };

const AXIS_INDEX: Record<string, number> = { x: 0, y: 1, z: 2 };

/** Unique node name `base`, `base2`, ... */
export function uniqueNodeName(nodes: readonly SkinNode[], base: string): string {
  const names = new Set(nodes.map((n) => n.name));
  if (!names.has(base)) return base;
  let i = 2;
  while (names.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}

/** Remove node `index`; its children move up to its parent. */
export function removeSkinNode(nodes: readonly SkinNode[], index: number): SkinNode[] {
  const gone = nodes[index];
  if (!gone) return nodes.slice();
  return nodes
    .filter((_, i) => i !== index)
    .map((n) => (gone.name && n.parent === gone.name ? patch(n, 'parent', gone.parent) : n));
}

/** Rename node `index`, repointing its children. */
export function renameSkinNode(nodes: readonly SkinNode[], index: number, name: string): SkinNode[] {
  const old = nodes[index]?.name;
  return nodes.map((n, i) => (i === index ? { ...n, name } : old && n.parent === old ? { ...n, parent: name } : n));
}

/** Add a child of node `parent` (null = a new root) at `position`. */
export function addSkinNode(nodes: readonly SkinNode[], parent: number | null, position: number[]): SkinNode[] {
  const p = parent !== null ? nodes[parent] : undefined;
  const radius = p ? Math.round((num(p.radius) ?? Math.max(...(vec(p.radii, 2) ?? [0.05]))) * 0.8 * 1000) / 1000 : 0.05;
  const node: SkinNode = { name: uniqueNodeName(nodes, p?.name ? `${p.name}_child` : 'node'), position, radius };
  if (p?.name) node.parent = p.name;
  return [...nodes, node];
}

/** Nodes in tree order (roots first, children under parents) with their depth. */
export function skinTreeOrder(nodes: readonly SkinNode[]): { index: number; depth: number }[] {
  const names = new Map(nodes.map((n, i) => [n.name, i]));
  const children = new Map<number, number[]>();
  const roots: number[] = [];
  nodes.forEach((n, i) => {
    const p = n.parent !== undefined ? names.get(n.parent) : undefined;
    if (p === undefined || p === i) roots.push(i);
    else children.set(p, [...(children.get(p) ?? []), i]);
  });
  const out: { index: number; depth: number }[] = [];
  const seen = new Set<number>();
  const walk = (i: number, depth: number) => {
    if (seen.has(i)) return;
    seen.add(i);
    out.push({ index: i, depth });
    for (const c of children.get(i) ?? []) walk(c, depth + 1);
  };
  roots.forEach((r) => walk(r, 0));
  nodes.forEach((_, i) => walk(i, 0)); // cycles: still list every node
  return out;
}

/** Names of node `index` and all its descendants (not valid parents for it). */
function descendants(nodes: readonly SkinNode[], index: number): Set<string> {
  const out = new Set<string>();
  const stack = [nodes[index]?.name];
  while (stack.length) {
    const name = stack.pop();
    if (!name || out.has(name)) continue;
    out.add(name);
    for (const n of nodes) if (n.parent === name) stack.push(n.name);
  }
  return out;
}

const nodeRadius = (n: SkinNode) => num(n.radius) ?? Math.max(...(vec(n.radii, 2) ?? [0.05]));

function NodeFields({ nodes, index, onChange }: { nodes: SkinNode[]; index: number; onChange: (nodes: SkinNode[]) => void }) {
  const node = nodes[index]!;
  const setNode = (key: string, v: unknown) => onChange(nodes.map((n, i) => (i === index ? patch(n, key, v) : n)));
  const blocked = descendants(nodes, index);
  const elliptical = vec(node.radii, 2) !== undefined;
  return (
    <>
      <Text label="Name" hint="Joint name (unique; children follow a rename)." value={str(node.name)} onChange={(v) => v && onChange(renameSkinNode(nodes, index, v))} />
      <Enum
        label="Parent"
        hint="Parent node name (none = a root)."
        value={str(node.parent)}
        unset="None (root)"
        options={nodes.flatMap((n) => (n.name && !blocked.has(n.name) ? [n.name] : []))}
        onChange={(v) => setNode('parent', v)}
      />
      <Vec label="Position" hint="Node position (part space, m). Front is -Z, ground at y = 0." value={vec(node.position, 3)} onChange={(v) => setNode('position', v)} />
      <Enum
        label="Section"
        hint="Round (radius) or elliptical cross-section [a, b]: a across the bone towards X (towards Z for bones running along X), b the other perpendicular."
        value={elliptical ? 'radii' : 'radius'}
        options={[
          { value: 'radius', label: 'Round' },
          { value: 'radii', label: 'Elliptical' },
        ]}
        onChange={(m) => {
          const r = nodeRadius(node);
          const next = { ...node };
          if (m === 'radii') {
            next.radii = [r, r];
            delete next.radius;
          } else {
            next.radius = r;
            delete next.radii;
          }
          onChange(nodes.map((n, i) => (i === index ? next : n)));
        }}
      />
      {elliptical ? (
        <Vec label="Radii" hint="Elliptical cross-section [a, b] (m)." size={2} labels={['A', 'B']} step={0.005} value={vec(node.radii, 2)} onChange={(v) => setNode('radii', v)} />
      ) : (
        <Num label="Radius" hint="Thickness around the node (m)." min={0} step={0.005} value={num(node.radius)} fallback={0.05} onChange={(v) => setNode('radius', v)} />
      )}
      <Color label="Color" hint="sRGB vertex color around this node (blended smoothly; unset = white)." value={vec(node.color, 3)} onChange={(v) => setNode('color', v)} />
    </>
  );
}

/** `skin`: a stick skeleton of nodes with radii → one smooth surface plus a rig. */
export function SkinEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const nodes = arr<unknown>(shape.nodes).map((n) => (obj(n) ?? {}) as SkinNode);
  const setNodes = (next: SkinNode[]) => set('nodes', next);
  const names = new Map(nodes.map((n, i) => [n.name, i]));
  const edges = nodes.flatMap((n, i) => {
    const p = n.parent !== undefined ? names.get(n.parent) : undefined;
    return p !== undefined && p !== i ? [[p, i] as const] : [];
  });
  const positions = nodes.map((n) => (vec(n.position, 3) ?? [0, 0, 0]) as Vec3);
  const axis = AXIS_INDEX[str(shape.symmetry) ?? ''];
  const mirror = (p: Vec3): Vec3 => (axis === undefined ? p : (p.map((v, k) => (k === axis ? -v : v)) as Vec3));
  const ghosts =
    axis === undefined
      ? []
      : edges.map(([a, b]) => ({ points: [mirror(positions[a]!), mirror(positions[b]!)], closed: false }));

  const applyNodeEdits = (pts: Vec3[], edits: PointEdit[]) => {
    let next = nodes.slice();
    for (const e of edits) {
      if (e.kind === 'delete') next = removeSkinNode(next, e.index);
      else next = addSkinNode(next, e.kind === 'add' ? (e.from ?? null) : null, [0, 0, 0]);
    }
    setNodes(next.map((n, i) => (pts[i] ? { ...n, position: pts[i] } : n)));
  };

  return (
    <>
      <Section title="Skeleton" hint="Nodes in three views: click to add a child of the selected node (or a root), drag to move, Del to remove.">
        <PathEditor
          points={positions}
          edges={edges}
          insert={false}
          radii={nodes.map(nodeRadius)}
          ghosts={ghosts}
          pointLabel={(i) => nodes[i]?.name || `#${i}`}
          onChange={applyNodeEdits}
          renderPoint={(i) => (nodes[i] ? <NodeFields nodes={nodes} index={i} onChange={setNodes} /> : null)}
        />
      </Section>
      <Section title="Nodes" hint="Nodes form a tree (each names its parent); every parent-child pair becomes a tapered capsule.">
        {skinTreeOrder(nodes).map(({ index, depth }) => {
          const n = nodes[index]!;
          return (
            <details key={index} className="shape-list-item" style={{ marginLeft: depth * 10 }}>
              <summary>
                <span className="shape-list-item-title">{n.name || <em className="muted">unnamed</em>}</span>
                <span className="skin-node-meta">{nodeRadius(n).toFixed(3)}</span>
                <span className="shape-list-item-actions" onClick={(e) => e.preventDefault()}>
                  <IconButton
                    icon="plus"
                    title="Add child node"
                    size={12}
                    onClick={() => {
                      const p = positions[index]!;
                      setNodes(addSkinNode(nodes, index, [p[0], p[1] + nodeRadius(n) * 2, p[2]]));
                    }}
                  />
                  <IconButton icon="trash" title="Remove node (children move to its parent)" size={12} onClick={() => setNodes(removeSkinNode(nodes, index))} />
                </span>
              </summary>
              <div className="shape-list-body">
                <NodeFields nodes={nodes} index={index} onChange={setNodes} />
              </div>
            </details>
          );
        })}
        <button type="button" className="add-component" onClick={() => setNodes(addSkinNode(nodes, null, [0, 0.5, 0]))}>
          Add root node
        </button>
      </Section>
      <Section title="Surface">
        <Enum
          label="Symmetry"
          hint="Mirror every node off the plane through the origin perpendicular to this axis. Names get .R (positive side) / .L (negative side)."
          value={str(shape.symmetry)}
          unset="None"
          options={['x', 'y', 'z']}
          onChange={(v) => set('symmetry', v)}
        />
        <Num label="Smooth" hint="Smoothing passes (volume-preserving) after meshing." integer min={0} value={num(shape.smooth)} fallback={2} onChange={(v) => set('smooth', v)} />
        <Num
          label="Resolution"
          hint="Grid cells along the longest side (refined so the thinnest node is at least two cells across)."
          integer
          min={8}
          step={8}
          value={num(shape.resolution)}
          fallback={128}
          onChange={(v) => set('resolution', v)}
        />
        <Num label="Detail" hint="Cell size in meters; overrides resolution when > 0." min={0} step={0.001} value={num(shape.detail)} fallback={0} onChange={(v) => set('detail', v)} />
        <Num
          label="Blend"
          hint="Smooth-blend width between branches, as a fraction of the thinner radius where they meet (0 = hard creases, 0.5 default, 1+ soft)."
          min={0}
          step={0.05}
          value={num(shape.blend)}
          fallback={0.5}
          onChange={(v) => set('blend', v)}
        />
        <Num
          label="Max triangles"
          hint="Triangle budget: denser surfaces are decimated (shape preserving) down to this. 0 = keep everything."
          integer
          min={0}
          step={1000}
          value={num(shape.maxTriangles)}
          fallback={40000}
          onChange={(v) => set('maxTriangles', v)}
        />
        <Num
          label="Ground"
          hint="Cut the surface flat at this height (usually 0): hooves, paws, soles and bellies rest on a real flat contact."
          optional
          value={num(shape.ground)}
          onChange={(v) => set('ground', v)}
        />
      </Section>
      <Section title="Masses" open={arr(shape.masses).length > 0}>
        <ListEditor
          title="Masses"
          hint="Muscle / fat masses (or carved hollows) blended into the surface: they shape the body but add no joints. Mirrored by symmetry when off the plane."
          items={arr<unknown>(shape.masses).map((m) => obj(m) ?? {})}
          onChange={(v) => set('masses', v.length ? v : undefined)}
          make={() => ({ name: '', position: [0, 0.5, 0], radii: [0.05, 0.05, 0.05] })}
          addLabel="Add mass"
          itemTitle={(m, i) => `${str(m.name) || `mass ${i + 1}`}${bool(m.subtract) ? ' (carve)' : ''}`}
          render={(m, setM) => {
            const s = (k: string, v: unknown) => setM(patch(m, k, v));
            return (
              <>
                <Text label="Name" hint="Optional label (shoulder, haunch, calf...)." value={str(m.name)} onChange={(v) => s('name', v)} />
                <Vec label="Position" hint="Ellipsoid center (part space, m)." value={vec(m.position, 3)} onChange={(v) => s('position', v)} />
                <Vec label="Radii" hint="Semi-axes (m)." step={0.005} value={vec(m.radii, 3) ?? [0.05, 0.05, 0.05]} onChange={(v) => s('radii', v)} />
                <Vec label="Rotation" hint="Euler XYZ degrees." step={1} value={vec(m.rotation, 3)} onChange={(v) => s('rotation', v)} />
                <Num label="Blend" hint="Smooth-blend width in meters (default: half the smallest radius)." optional min={0} step={0.005} value={num(m.blend)} onChange={(v) => s('blend', v)} />
                <Bool label="Subtract" hint="Carve instead of add (tucked belly, gill slit)." value={bool(m.subtract)} onChange={(v) => s('subtract', v || undefined)} />
              </>
            );
          }}
        />
      </Section>
    </>
  );
}
